/**
 * Operator-visible notifications timeline for a customer.
 *
 * Reads /api/customers/:id/notifications and renders every outbound
 * message we've sent (or tried to send). Two status dimensions:
 *
 *   - `status` (sent / pending / failed) — provider acceptance. This is
 *     the immediate response from NextSMS's submit endpoint.
 *   - `deliveryStatus` (DELIVERED_TO_HANDSET / REJECTED / etc) — handset
 *     feedback that NextSMS POSTs back to our DLR webhook minutes later.
 *
 * A row can be status=sent + deliveryStatus=REJECTED (provider accepted
 * but the carrier later returned it). Both columns show; ops uses the
 * combination to decide whether to retry / escalate.
 */
import { useQuery } from "@tanstack/react-query";
import type { NotificationOutbound } from "@dfp/api-client";
import { Badge, type BadgeTone } from "./Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { Table, TBody, THead, Th, Td, Tr } from "./Table.js";
import { api } from "../lib/api.js";

interface CustomerNotificationsPanelProps {
  readonly customerId: string;
}

export function CustomerNotificationsPanel({
  customerId,
}: CustomerNotificationsPanelProps): JSX.Element {
  const query = useQuery({
    queryKey: ["customers", "notifications", customerId],
    queryFn: () =>
      api.get<readonly NotificationOutbound[]>(`/api/customers/${customerId}/notifications`),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <span className="text-xs text-slate-500">{query.data?.length ?? 0}</span>
      </CardHeader>
      <CardBody className="p-0">
        {query.isLoading ? (
          <p className="p-5 text-sm text-slate-500">Loading…</p>
        ) : query.isError ? (
          <p className="p-5 text-sm text-red-700">Failed to load notifications.</p>
        ) : (query.data ?? []).length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No notifications sent to this customer.</p>
        ) : (
          <Table>
            <THead>
              <tr>
                <Th>Kind</Th>
                <Th>Channel</Th>
                <Th>Status</Th>
                <Th>Delivery</Th>
                <Th>Sent at</Th>
                <Th>Recipient</Th>
              </tr>
            </THead>
            <TBody>
              {(query.data ?? []).map((n) => (
                <Tr key={n.id}>
                  <Td>{prettyKind(n.kind)}</Td>
                  <Td className="text-xs uppercase tracking-wide text-slate-500">{n.channel}</Td>
                  <Td>
                    <Badge tone={statusTone(n.status)}>{n.status}</Badge>
                  </Td>
                  <Td>
                    {n.deliveryStatus === null ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <Badge tone={deliveryTone(n.deliveryStatus)}>{n.deliveryStatus}</Badge>
                    )}
                    {n.deliveryDescription === null ? null : (
                      <p className="mt-1 text-xs text-slate-500">{n.deliveryDescription}</p>
                    )}
                  </Td>
                  <Td className="text-xs text-slate-600">
                    {n.sentAt === null ? "—" : new Date(n.sentAt).toLocaleString()}
                  </Td>
                  <Td className="font-mono text-xs">{maskRecipient(n.recipient)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function statusTone(status: NotificationOutbound["status"]): BadgeTone {
  switch (status) {
    case "sent":
      return "success";
    case "failed":
      return "danger";
    case "pending":
    default:
      return "neutral";
  }
}

/**
 * Map the provider-specific delivery vocabulary onto a tone. We don't
 * enumerate every NextSMS status — anything starting with REJECTED /
 * EXPIRED / UNDELIVERABLE is bad; DELIVERED_TO_HANDSET is good;
 * everything else is neutral (PENDING_*, ENROUTE).
 */
function deliveryTone(deliveryStatus: string): BadgeTone {
  const s = deliveryStatus.toUpperCase();
  if (s.includes("DELIVERED")) return "success";
  if (s.startsWith("REJECTED") || s.startsWith("EXPIRED") || s.includes("UNDELIVERABLE")) {
    return "danger";
  }
  return "neutral";
}

function prettyKind(kind: NotificationOutbound["kind"]): string {
  // Keep the enum mapping inline so adding a kind here AND in the api-
  // client type forces both to update.
  switch (kind) {
    case "device_release_confirmed":
      return "Device release confirmed";
    case "payment_receipt":
      return "Payment receipt";
    case "customer_otp":
      return "OTP code";
    case "customer_welcome":
      return "Welcome message";
    default:
      return kind;
  }
}

/**
 * Mask the recipient phone (PII) — last 4 only. Operators recognise
 * customers by the masked tail, full numbers don't belong on the screen.
 */
function maskRecipient(raw: string): string {
  if (raw.length <= 4) return "****";
  return `${"*".repeat(raw.length - 4)}${raw.slice(-4)}`;
}
